import {PayloadAction, createSlice} from "@reduxjs/toolkit";

import {combineLatest,  Observable, of} from 'rxjs';
import {map, switchMap, filter, withLatestFrom} from 'rxjs/operators';

import {ReceiverState} from "../types";
import {Receiver} from "common";
import {ActionTypes, RootState} from "./store";
import {updateUser} from "./auth";
import {updateGiftExchange} from "./giftExchange";
import {Epic} from "redux-observable";
import {collection, onSnapshot, query, where} from "firebase/firestore";
import {db} from "./firebase";

const initialState: ReceiverState = {
  receivers: [],
  isInitializing: true,
};

export const slice = createSlice({
  name: 'myReceivers',
  initialState,
  reducers: {
    updateReceivers: (state, action: PayloadAction<Receiver[]>) => {
      const {receivers: existingReceivers} = state;
      const {payload: newReceivers} = action;

      // Remove any dups
      state.receivers = [
        ...newReceivers,
        ...existingReceivers.filter((ep) => newReceivers.findIndex((p) => p.pid === ep.pid) === -1)
      ].sort((r1: Receiver, r2: Receiver) => r1.name < r2.name ? -1 : 1);
      state.isInitializing = false;
    },
    deleteReceivers: (state, action: PayloadAction<string[]>) => {
      const {receivers: existingReceivers} = state;
      const {payload: receiversToDelete} = action;

      // Remove receiver if id is in the action list
      state.receivers = existingReceivers.filter((r: Receiver) => !receiversToDelete.includes(r.pid));
    },
    initComplete: (state) => {
      state.isInitializing = false;
    },
  },
});

export const {
  updateReceivers,
  deleteReceivers,
  initComplete,
} = slice.actions;

export type MyReceiverActionsTypes = 
  ReturnType<typeof updateReceivers> |
  ReturnType<typeof deleteReceivers> |
  ReturnType<typeof initComplete>
;

export const loadMyReceiversEpic: Epic<ActionTypes, ActionTypes, RootState> = (action$, state$) => {
  const user$ = action$.pipe(filter(updateUser.match));
  const giftExchange$ = action$.pipe(filter(updateGiftExchange.match));
  return combineLatest([user$, giftExchange$]).pipe(
    withLatestFrom(state$),
    map(([,state]) => state),
    filter((state) => !state.auth.isInitializing && !state.giftExchange.isInitializing),
    filter((state) => state.auth.isLoggedIn),
    filter((state) => state.giftExchange.isActive),
    switchMap((state) =>
      (state.auth.user?.canAccess?.length || 0) === 0 ?
      of(initComplete()) :
      new Observable<MyReceiverActionsTypes>((subscriber) => {
        const {canAccess} = state.auth.user || {};
        const receiversCollection = collection(db, "gift-exchanges", String(state.giftExchange.currentGiftExchange?.gid), "receivers");
        const receiversQuery = query(receiversCollection, where("pid", "in", canAccess));

        const unsubscribe = onSnapshot(receiversQuery, {
          next: (snapshot) => {
            const receiversToUpdate: Receiver[] = [];
            const receiversToDelete: string[] = [];
            snapshot.docChanges().forEach((change) => {
              const {doc} = change;
              if (change.type === "removed") {
                receiversToDelete.push(doc.id);
              } else {
                const data = doc.data();
                const {name, giftIdeas, spendingLimit, attending, deliveryInstructions} = data;
                receiversToUpdate.push({
                  pid: doc.id,
                  name,
                  giftIdeas,
                  spendingLimit,
                  attending,
                  deliveryInstructions,
                  messages: [],
                });
              }
            });
            if (receiversToUpdate.length > 0) {
              subscriber.next(updateReceivers(receiversToUpdate));
            }
            if (receiversToDelete.length > 0) {
              subscriber.next(deleteReceivers(receiversToDelete));
            }
            if (state.myReceivers.isInitializing) {
              subscriber.next(initComplete());
            }
          },
          error: (error) => {
            console.error("My receivers query errored", error);
            subscriber.error(error);
          },
          complete: () => {
            console.warn("My receivers query closed");
            subscriber.complete();
          },
        });
        return unsubscribe;
      })
    ),
  );
};

export default slice.reducer;
